import { createURL } from '@sm/utils';
import { Desc } from '@sm/intl';
import { LANGUAGES_BY_CODE, LANGUAGES_BY_ID } from '@sm/locale';
import { US_GEO_PAGES, WeightedElement } from './constants';
import { GetCollectorByIdQuery } from '~app/helpers/gql/GetCollectorById.types';

/**
 * Using a weighted distribution, select and return the page that the target element falls on
 *
 * @param weightedElements object of pages
 * @param target value within distribution range, use a random number [0, 1) if weighted distribution adds up to 1
 * @returns Selected page object to serve
 */
export function WeightedElementSelector(weightedElements: WeightedElement[], target: number): WeightedElement {
  let cumulativeWeight = 0.0;
  let selectedPage: WeightedElement = US_GEO_PAGES[0];
  weightedElements.every(element => {
    cumulativeWeight += element.weight;
    if (target < cumulativeWeight) {
      selectedPage = element;
      return false;
    }
    return true;
  });
  return selectedPage;
}

export function checkWeightsEqualToOne(weightedElements: WeightedElement[]): boolean {
  let total = 0.0;
  weightedElements.forEach(element => {
    total += element.weight;
  });
  if (Number(total.toFixed(3)) === 1) return true;
  return false;
}

// Parse language ID safely
export const getParsedLanguageId = (languageId: string): number => parseInt(languageId, 10) || LANGUAGES_BY_CODE.en.id;

// Determine branding status
export const checkIsNonBranded = (isNonBrandedSmParam: boolean, collectorData: GetCollectorByIdQuery): boolean =>
  isNonBrandedSmParam || (collectorData.collector?.hideBranding ?? false);

// Determine embedded status
export const checkIsEmbeddedPage = (isEmbeddedSmParam: boolean, collectorType: string): boolean =>
  isEmbeddedSmParam || collectorType?.toUpperCase() === 'POPUP';

export const getSurveyLanguage = (languageId: number | string): string => {
  const parsedId = parseInt(languageId as string, 10) || LANGUAGES_BY_CODE.en.id;
  return LANGUAGES_BY_ID[parsedId]?.code ?? LANGUAGES_BY_CODE.en.code;
};

export const getCollectorId = (collectorId: string): number | null => {
  return collectorId ? parseInt(collectorId, 10) : null;
};

export const getUrlWithUtsource3 = (url: string, value: string): string => url.replaceAll('UTSOURCE3_VALUE', value);

export type PrivacyNoticeValues = Record<string, string>;
export type PrivacyNotice = [Desc<string, string, string>, PrivacyNoticeValues];

// Tracking parameters to a URL for marketing attribution
export function addTrackingParams(
  url: string,
  utSource: string,
  utSource2?: string,
  utSource3?: string,
  additionalParams: Record<string, string> = {}
): string {
  const params = {
    ut_source: utSource,
    ...(utSource2 && { ut_source2: utSource2 }),
    ...(utSource3 && { ut_source3: utSource3 }),
    ...additionalParams,
  };

  return createURL(url, params);
}

export function enhancePrivacyNoticeWithTracking(
  privacyNotice: PrivacyNotice | undefined,
  utSource: string,
  utSource2?: string,
  utSource3?: string,
  additionalParams?: Record<string, string>
): PrivacyNotice | undefined {
  if (!privacyNotice || !Array.isArray(privacyNotice) || privacyNotice.length < 2) {
    return privacyNotice;
  }

  const [messageDescriptor, values] = privacyNotice;

  const enhancedValues = Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      ['link', 'SurveyMonkeyPrivacyPolicyLink', 'UPennPrivacyPolicyLink'].includes(key)
        ? addTrackingParams(value, utSource, utSource2, utSource3, additionalParams)
        : value,
    ])
  );

  return [messageDescriptor, enhancedValues];
}
